import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Snackbar,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppRoutes from './components/AppRoutes';
import HeaderBar from './components/Header';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import DebugDataWindow from './components/DebugDataWindow';
import { isPreviewFrame } from './domain/categoryPermissions';

import './App.css';
import { useAuth } from './contexts/AuthContext';
import { useCashDrawer } from './contexts/CashDrawerContext';
import { useDebugData } from './contexts/DebugDataContext';
import { SumatorProvider } from './contexts/SumatorContext';
import SumatorWidget from './components/SumatorWidget';
import BridgeSumatorSync from './components/BridgeSumatorSync';
import PendingTransfersModal from './components/PendingTransfersModal';
import HelpButton from './components/help/HelpButton';
import AskAiButton from './components/aiAssistant/AskAiButton';
import useRequest from './hooks/useRequest';
// Page components are provided via `routeConfig` to keep routing centralized

interface CashPoint {
    id: number;
    name: string;
    isCashPoint?: boolean;
}

interface CashPointLookupResponse {
    found: boolean;
    cashPoint: CashPoint | null;
}

interface CashPointRegisterResponse {
    success: boolean;
    cashPoint: CashPoint;
    isNew: boolean;
}

const App = () => {
    const { user } = useAuth();
    const { refreshBalance, setCashPointId } = useCashDrawer();
    const { initializeCashSession, isSessionInitialized, updateCashPointData } = useDebugData();
    const [collapsed, setCollapsed] = useState(false);
    const request = useRequest();
    const { t } = useTranslation();

    const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

    // Station/cashpoint registration state
    const [stationName, setStationName] = useState<string>('');
    const [currentCashPoint, setCurrentCashPoint] = useState<CashPoint | null>(null);
    const [cashPointInitialized, setCashPointInitialized] = useState(false);

    // Register new cashpoint dialog state
    const [registerCashPointDialogOpen, setRegisterCashPointDialogOpen] = useState(false);
    const [registerAsCashPoint, setRegisterAsCashPoint] = useState(true);
    const [registerError, setRegisterError] = useState<string | null>(null);
    const [registerSaving, setRegisterSaving] = useState(false);
    const [pcNameInput, setPcNameInput] = useState<string>('');

    // Initial cash setup dialog state (only shown for first-time cashpoints)
    const [initialCashDialogOpen, setInitialCashDialogOpen] = useState(false);
    const [initialAmountRon, setInitialAmountRon] = useState<string>('');
    const [initialCashSaving, setInitialCashSaving] = useState(false);
    const [initialCashError, setInitialCashError] = useState<string | null>(null);
    const [initialCashSuccess, setInitialCashSuccess] = useState(false);

    /**
     * Get the station name (PC name).
     * Since browsers cannot access the Windows PC name for security reasons,
     * we rely on localStorage where the user saves their PC name during registration.
     * Returns the saved PC name or empty string if not yet registered.
     */
    const getStationName = useCallback((): string => {
        const savedStationName = localStorage.getItem('stationName');
        return savedStationName ?? '';
    }, []);

    /** Apply a found cashpoint: persist state and check if initial handover is needed. */
    const applyFoundCashPoint = useCallback(
        async (cashPoint: CashPoint, isCancelled: () => boolean) => {
            setCurrentCashPoint(cashPoint);
            setCashPointInitialized(true);
            updateCashPointData(cashPoint.id, cashPoint.name);
            setCashPointId(cashPoint.id);

            // Only a cash register opens a session: money booked at a plain workstation sat in a
            // till no handover counts, and the server now refuses it (cash session audit item 11).
            if (!cashPoint.isCashPoint) return;

            if (!isSessionInitialized) {
                await initializeCashSession(cashPoint.id);
            }

            const status = await request<{ exists: boolean; lastHandoverAmount: number }>({
                path: `/cash-drawer/initial-handover/${cashPoint.id}`,
            });
            if (isCancelled()) return;

            if (!status.exists) {
                setInitialCashDialogOpen(true);
            } else if (status.lastHandoverAmount > 0) {
                setInitialAmountRon(String(Math.round(status.lastHandoverAmount)));
            }
        },
        [request, isSessionInitialized, initializeCashSession, updateCashPointData, setCashPointId],
    );

    /**
     * Initialize the cashpoint on app startup.
     * This follows the Delphi pattern:
     * 1. Get computer name
     * 2. Look for it in cashpoints table
     * 3. If not found, ask to register it
     * 4. If found, use that cashpoint ID
     */
    useEffect(() => {
        let cancelled = false;

        const initializeCashPoint = async () => {
            if (!user || cashPointInitialized) return;

            try {
                const station = getStationName();
                setStationName(station);

                if (!station) {
                    setRegisterCashPointDialogOpen(true);
                    return;
                }

                const lookupResult = await request<CashPointLookupResponse>({
                    path: `/cash-drawer/cashpoint/lookup/${encodeURIComponent(station)}`,
                });
                if (cancelled) return;

                if (lookupResult.found && lookupResult.cashPoint) {
                    await applyFoundCashPoint(lookupResult.cashPoint, () => cancelled);
                } else {
                    setRegisterCashPointDialogOpen(true);
                }
            } catch (error) {
                console.error('Cashpoint initialization failed:', error);
                setCashPointInitialized(true);
            }
        };

        void initializeCashPoint();
        return () => {
            cancelled = true;
        };
    }, [request, user, cashPointInitialized, getStationName, applyFoundCashPoint]);

    // Reset state when user logs out
    useEffect(() => {
        if (!user) {
            setCashPointInitialized(false);
            setCurrentCashPoint(null);
            setStationName('');
            // Reset cash drawer cashpoint when user logs out
            setCashPointId(null);
        }
    }, [user, setCashPointId]);

    if (!user) {
        return <Login />;
    }

    // The small preview of a page in Configuration > User categories: just the page, none of the
    // application around it, and nothing that would open a dialog over it.
    if (isPreviewFrame(window)) {
        return (
            <Box data-testid="page-preview-content" sx={{ p: 2 }}>
                <SumatorProvider>
                    <AppRoutes guarded={false} />
                </SumatorProvider>
            </Box>
        );
    }

    /**
     * Handle registering the station as a new cashpoint
     */
    const handleRegisterCashPoint = async () => {
        const trimmedPcName = pcNameInput.trim().toUpperCase();
        if (!trimmedPcName) {
            setRegisterError(t('cashDrawer.register.pcNameRequired'));
            return;
        }

        try {
            setRegisterSaving(true);
            setRegisterError(null);

            const result = await request<CashPointRegisterResponse>({
                path: '/cash-drawer/cashpoint/register',
                method: 'POST',
                params: {
                    stationName: trimmedPcName,
                    isCashPoint: registerAsCashPoint,
                },
            });

            if (result.success && result.cashPoint) {
                // Save PC name to localStorage for future sessions
                localStorage.setItem('stationName', trimmedPcName);
                setStationName(trimmedPcName);
                setCurrentCashPoint(result.cashPoint);
                setCashPointInitialized(true);
                setRegisterCashPointDialogOpen(false);

                // Update debug data with cashpoint info
                updateCashPointData(result.cashPoint.id, result.cashPoint.name);

                // Set cashpoint ID in CashDrawerContext so balance is fetched correctly
                setCashPointId(result.cashPoint.id);

                // A cash register opens its session and asks for its opening cash; a plain
                // workstation opens none (cash session audit item 11).
                if (registerAsCashPoint) {
                    await initializeCashSession(result.cashPoint.id);
                    setInitialCashDialogOpen(true);
                }
            }
        } catch (error) {
            setRegisterError(
                error instanceof Error ? error.message : t('cashDrawer.register.saveFailed'),
            );
        } finally {
            setRegisterSaving(false);
        }
    };

    /**
     * Handle skipping cashpoint registration (station will not be a cashpoint)
     */
    const handleSkipRegistration = async () => {
        const trimmedPcName = pcNameInput.trim().toUpperCase();
        if (!trimmedPcName) {
            setRegisterError(t('cashDrawer.register.pcNameRequired'));
            return;
        }

        try {
            setRegisterSaving(true);
            setRegisterError(null);

            // Register as non-cashpoint station
            const result = await request<CashPointRegisterResponse>({
                path: '/cash-drawer/cashpoint/register',
                method: 'POST',
                params: {
                    stationName: trimmedPcName,
                    isCashPoint: false,
                },
            });

            if (result.success && result.cashPoint) {
                // Save PC name to localStorage for future sessions
                localStorage.setItem('stationName', trimmedPcName);
                setStationName(trimmedPcName);
                setCurrentCashPoint(result.cashPoint);
                setCashPointInitialized(true);
                setRegisterCashPointDialogOpen(false);

                // Update debug data with cashpoint info
                updateCashPointData(result.cashPoint.id, result.cashPoint.name);

                // Set cashpoint ID in CashDrawerContext so balance is fetched correctly
                setCashPointId(result.cashPoint.id);
                // No cash session: a plain workstation books no money (cash session audit item 11).
            }
        } catch (error) {
            console.error('Failed to skip registration:', error);
            // Still close dialog and continue
            setCashPointInitialized(true);
            setRegisterCashPointDialogOpen(false);
        } finally {
            setRegisterSaving(false);
        }
    };

    const handleSaveInitialCash = async () => {
        const amount = Number(initialAmountRon);
        if (!currentCashPoint) {
            setInitialCashError(t('cashDrawer.initialCash.cashPointRequired'));
            return;
        }

        if (!Number.isFinite(amount) || amount < 0) {
            setInitialCashError(t('cashDrawer.initialCash.amountInvalid'));
            return;
        }

        try {
            setInitialCashSaving(true);
            setInitialCashError(null);

            await request<{ success: boolean }>({
                path: '/cash-drawer/initial-handover',
                method: 'POST',
                params: {
                    cashPointId: currentCashPoint.id,
                    initialAmountRon: amount,
                    date: todayStr,
                },
            });

            // Refresh cash drawer balance after initial setup
            void refreshBalance();

            setInitialCashDialogOpen(false);
            setInitialCashSuccess(true);
        } catch (e) {
            setInitialCashError(
                e instanceof Error ? e.message : t('cashDrawer.initialCash.saveFailed'),
            );
        } finally {
            setInitialCashSaving(false);
        }
    };

    return (
        <Box display="flex" height="100vh">
            <Sidebar collapsed={collapsed} />
            <Box
                display="flex"
                flexDirection="column"
                flex={1}
                // minWidth 0: the window decides the width, not the widest header or page
                // content, so nothing pushes the whole page past the right edge.
                sx={{ marginLeft: collapsed ? '40px' : '50px', minWidth: 0 }}
            >
                <HeaderBar collapsed={collapsed} setCollapsed={setCollapsed} />
                <PendingTransfersModal />
                <Box component="main" sx={{ margin: 3, flex: 1, overflow: 'auto' }}>
                    {/* Register Cashpoint Dialog */}
                    <Dialog
                        open={registerCashPointDialogOpen}
                        onClose={() => undefined}
                        aria-labelledby="register-cashpoint-dialog-title"
                    >
                        <DialogTitle id="register-cashpoint-dialog-title">
                            {t('cashDrawer.register.title')}
                        </DialogTitle>
                        <DialogContent sx={{ minWidth: 420 }}>
                            {registerError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {registerError}
                                </Alert>
                            )}
                            <DialogContentText sx={{ mb: 2 }}>
                                {t('cashDrawer.register.message')}
                            </DialogContentText>
                            <TextField
                                fullWidth
                                label={t('cashDrawer.register.pcNameLabel')}
                                placeholder={t('cashDrawer.register.pcNamePlaceholder')}
                                value={pcNameInput}
                                onChange={e => setPcNameInput(e.target.value.toUpperCase())}
                                helperText={t('cashDrawer.register.pcNameHint')}
                                sx={{ mb: 2 }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={registerAsCashPoint}
                                        onChange={e => setRegisterAsCashPoint(e.target.checked)}
                                    />
                                }
                                label={t('cashDrawer.register.isCashPoint')}
                            />
                            <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ ml: 6 }}
                            >
                                {t('cashDrawer.register.isCashPointHint')}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleSkipRegistration}
                                disabled={registerSaving || !pcNameInput.trim()}
                            >
                                {t('cashDrawer.register.skipButton')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleRegisterCashPoint}
                                disabled={registerSaving || !pcNameInput.trim()}
                            >
                                {registerSaving
                                    ? t('common.saving')
                                    : t('cashDrawer.register.registerButton')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Initial Cash Setup Dialog */}
                    <Dialog
                        open={initialCashDialogOpen}
                        onClose={() => undefined}
                        aria-labelledby="initial-cash-dialog-title"
                    >
                        <DialogTitle id="initial-cash-dialog-title">
                            {t('cashDrawer.initialCash.title')}
                        </DialogTitle>
                        <DialogContent sx={{ minWidth: 420 }}>
                            {initialCashError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {initialCashError}
                                </Alert>
                            )}

                            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                                {t('cashDrawer.initialCash.cashPoint')}:{' '}
                                <strong>{currentCashPoint?.name ?? stationName}</strong>
                            </Typography>

                            <TextField
                                fullWidth
                                sx={{ mt: 2 }}
                                type="number"
                                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                                label={t('cashDrawer.initialCash.amountRon')}
                                value={initialAmountRon}
                                onChange={e => setInitialAmountRon(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                onClick={handleSaveInitialCash}
                                disabled={initialCashSaving}
                            >
                                {initialCashSaving
                                    ? t('common.saving')
                                    : t('cashDrawer.initialCash.save')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <SumatorProvider>
                        <AppRoutes guarded />
                        <SumatorWidget />
                        <BridgeSumatorSync />
                        {/* Inside the provider: the button steps aside while the Sumator is open. */}
                        <AskAiButton />
                    </SumatorProvider>
                    <HelpButton collapsed={collapsed} />
                </Box>
            </Box>

            {/* Success Snackbar for Initial Cash */}
            <Snackbar
                open={initialCashSuccess}
                autoHideDuration={6000}
                onClose={() => setInitialCashSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setInitialCashSuccess(false)}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {t('cashDrawer.initialCash.saveSuccess')}
                </Alert>
            </Snackbar>

            {/* Debug Data Window - visible for admins only */}
            <DebugDataWindow />
        </Box>
    );
};

export default App;
